import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Compass, Layers, ListTodo, Gavel, Scale, Shield, ChevronRight,
  Target, Activity, Calendar, Star, CircleDot, CheckCircle2, Zap,
  Clock, ArrowLeft, FileText, AlertTriangle, BarChart3, Users,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import {
  getGestorDashboard, getPlanDirectives, getPlanDecisions, getPlanTasks,
  getPlanExceptions, PHASE_COLORS, AGENT_LABELS, DIRECTIVE_STATUS,
  DECISION_TYPE_LABELS, DECISION_TYPE_COLORS,
} from '../services/gestorService';

// ═══════════════════════════════════════════════════════════════════════════════
// Tabs
// ═══════════════════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'overview', label: 'Visão Geral', icon: Compass },
  { key: 'directives', label: 'Diretrizes', icon: Layers },
  { key: 'tasks', label: 'Tarefas', icon: ListTodo },
  { key: 'decisions', label: 'Decisões', icon: Gavel },
  { key: 'exceptions', label: 'Exceções', icon: Scale },
];

const TabBar = ({ active, onChange }) => (
  <div className="flex gap-1 overflow-x-auto pb-1 mb-6">
    {TABS.map((tab) => {
      const isActive = active === tab.key;
      return (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all"
          style={{
            backgroundColor: isActive ? VUKZ.primary : 'white',
            color: isActive ? 'white' : VUKZ.textLight,
            border: `1px solid ${isActive ? VUKZ.primary : VUKZ.border}`,
          }}
        >
          <tab.icon size={14} />
          {tab.label}
        </button>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

const AlignmentBar = ({ score }) => {
  const color = score >= 80 ? VUKZ.success : score >= 65 ? VUKZ.warning : VUKZ.error;
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-bold min-w-[32px] text-right" style={{ color }}>{score}%</span>
    </div>
  );
};

const StatusBadge = ({ label, bg, text }) => (
  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: bg, color: text }}>
    {label}
  </span>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Tab: Overview
// ═══════════════════════════════════════════════════════════════════════════════

const OverviewTab = ({ plan }) => {
  const today = new Date();
  const start = new Date(plan.period_start);
  const end = new Date(plan.period_end);
  const totalDays = Math.max(1, (end - start) / 86400000);
  const elapsedDays = Math.max(0, (today - start) / 86400000);
  const progressPct = Math.min(100, Math.round((elapsedDays / totalDays) * 100));

  return (
    <div className="space-y-6">
      {/* North Star */}
      <div className="bg-white rounded-2xl border p-5" style={{ borderColor: VUKZ.border }}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#fefce8' }}>
            <Star size={18} style={{ color: '#ca8a04' }} />
          </div>
          <div>
            <h3 className="text-sm font-extrabold mb-1" style={{ color: VUKZ.text }}>North Star</h3>
            <p className="text-[12px] leading-relaxed" style={{ color: VUKZ.text }}>{plan.north_star}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4 p-3 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center relative" style={{ background: `conic-gradient(${VUKZ.primary} ${progressPct * 3.6}deg, ${VUKZ.primaryLight} 0)` }}>
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <span className="text-xs font-black" style={{ color: VUKZ.text }}>{progressPct}%</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold" style={{ color: VUKZ.text }}>
              Semana {Math.ceil(elapsedDays / 7)} de {plan.duration_weeks}
            </div>
            <div className="text-[11px]" style={{ color: VUKZ.textLight }}>
              {new Date(plan.period_start).toLocaleDateString('pt-BR')} → {new Date(plan.period_end).toLocaleDateString('pt-BR')}
            </div>
          </div>
        </div>
      </div>

      {/* Phases */}
      <div className="bg-white rounded-2xl border p-5" style={{ borderColor: VUKZ.border }}>
        <h3 className="text-sm font-extrabold mb-4" style={{ color: VUKZ.text }}>Fases do Macrociclo</h3>
        <div className="space-y-3">
          {(plan.phases || []).map((phase) => {
            const ps = PHASE_COLORS[phase.name] || PHASE_COLORS.Base;
            const isCurrent = today >= new Date(phase.start) && today <= new Date(phase.end);
            return (
              <div
                key={phase.name}
                className={`p-4 rounded-xl border ${isCurrent ? 'ring-2 ring-offset-2' : ''}`}
                style={{
                  backgroundColor: ps.bg,
                  borderColor: isCurrent ? ps.dot : 'transparent',
                  ringColor: isCurrent ? ps.dot : 'transparent',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CircleDot size={14} style={{ color: ps.dot }} />
                    <span className="text-sm font-extrabold" style={{ color: ps.text }}>{phase.name}</span>
                    {isCurrent && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${ps.dot}20`, color: ps.text }}>
                        FASE ATUAL
                      </span>
                    )}
                  </div>
                  <span className="text-[10px]" style={{ color: ps.text }}>
                    {new Date(phase.start).toLocaleDateString('pt-BR')} → {new Date(phase.end).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <p className="text-[11px] mb-2" style={{ color: ps.text }}>{phase.objectives}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(phase.kpis || []).map((kpi, i) => (
                    <span key={i} className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${ps.dot}15`, color: ps.text }}>
                      {kpi}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Global KPIs */}
      <div className="bg-white rounded-2xl border p-5" style={{ borderColor: VUKZ.border }}>
        <h3 className="text-sm font-extrabold mb-4" style={{ color: VUKZ.text }}>KPIs Globais</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(plan.global_kpis || []).map((kpi, i) => (
            <div key={i} className="p-3 rounded-xl border" style={{ borderColor: VUKZ.border }}>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 size={12} style={{ color: VUKZ.primary }} />
                <span className="text-[11px] font-bold" style={{ color: VUKZ.text }}>{kpi.metric}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#d1fae5' }}>
                  <div className="text-[10px] font-bold" style={{ color: '#065f46' }}>{kpi.target}</div>
                  <div className="text-[8px]" style={{ color: '#065f46' }}>Meta</div>
                </div>
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#fef3c7' }}>
                  <div className="text-[10px] font-bold" style={{ color: '#92400e' }}>{kpi.threshold_warning}</div>
                  <div className="text-[8px]" style={{ color: '#92400e' }}>Warning</div>
                </div>
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#fee2e2' }}>
                  <div className="text-[10px] font-bold" style={{ color: '#991b1b' }}>{kpi.threshold_critical}</div>
                  <div className="text-[8px]" style={{ color: '#991b1b' }}>Critical</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Tab: Directives
// ═══════════════════════════════════════════════════════════════════════════════

const DirectivesTab = ({ directives }) => (
  <div className="space-y-4">
    {directives.map((dir) => {
      const ds = DIRECTIVE_STATUS[dir.directive_status] || DIRECTIVE_STATUS.draft;
      return (
        <div key={dir.id} className="bg-white rounded-2xl border p-5" style={{ borderColor: dir.directive_status === 'needs_adjustment' ? VUKZ.warning : VUKZ.border }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-extrabold" style={{ color: VUKZ.text }}>{dir.category_name}</h3>
                <StatusBadge label={ds.label} bg={ds.bg} text={ds.text} />
              </div>
              <p className="text-[11px]" style={{ color: VUKZ.textLight }}>{dir.age_range}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-black" style={{ color: VUKZ.primary }}>{dir.avg_alignment || 0}</div>
              <div className="text-[9px]" style={{ color: VUKZ.textLight }}>Alignment</div>
            </div>
          </div>

          {/* Focus */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Target size={12} style={{ color: VUKZ.primary }} />
                <span className="text-[10px] font-bold" style={{ color: VUKZ.text }}>Foco Técnico</span>
              </div>
              <p className="text-[11px]" style={{ color: VUKZ.textLight }}>{dir.focus_technical}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Activity size={12} style={{ color: '#2563eb' }} />
                <span className="text-[10px] font-bold" style={{ color: VUKZ.text }}>Foco Físico</span>
              </div>
              <p className="text-[11px]" style={{ color: VUKZ.textLight }}>{dir.focus_physical}</p>
            </div>
          </div>

          {/* Load + Intensity Distribution */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="text-center p-2 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
              <div className="text-sm font-black" style={{ color: VUKZ.primary }}>{dir.weekly_load_min_km}–{dir.weekly_load_max_km}</div>
              <div className="text-[9px]" style={{ color: VUKZ.textLight }}>km/sem</div>
            </div>
            <div className="text-center p-2 rounded-lg" style={{ backgroundColor: '#dbeafe' }}>
              <div className="text-sm font-black" style={{ color: '#1e40af' }}>{dir.intensity_distribution?.aerobic}%</div>
              <div className="text-[9px]" style={{ color: '#1e40af' }}>Aeróbio</div>
            </div>
            <div className="text-center p-2 rounded-lg" style={{ backgroundColor: '#fef3c7' }}>
              <div className="text-sm font-black" style={{ color: '#92400e' }}>{dir.intensity_distribution?.threshold}%</div>
              <div className="text-[9px]" style={{ color: '#92400e' }}>Threshold</div>
            </div>
            <div className="text-center p-2 rounded-lg" style={{ backgroundColor: '#fce7f3' }}>
              <div className="text-sm font-black" style={{ color: '#9d174d' }}>{dir.intensity_distribution?.speed}%</div>
              <div className="text-[9px]" style={{ color: '#9d174d' }}>Velocidade</div>
            </div>
          </div>

          {/* Priority Objectives */}
          <div className="mb-3">
            <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>Objetivos Prioritários</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(dir.priority_objectives || []).map((obj, i) => (
                <span key={i} className="text-[9px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                  {obj}
                </span>
              ))}
            </div>
          </div>

          {/* Restrictions */}
          {dir.restrictions && dir.restrictions.length > 0 && (
            <div className="mb-3">
              <span className="text-[10px] font-bold" style={{ color: VUKZ.error }}>Restrições</span>
              <ul className="mt-1 space-y-0.5">
                {dir.restrictions.map((r, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-[10px]" style={{ color: VUKZ.text }}>
                    <AlertTriangle size={9} style={{ color: VUKZ.warning }} /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Checkpoints */}
          {dir.checkpoints && dir.checkpoints.length > 0 && (
            <div className="p-3 rounded-xl border mt-2" style={{ borderColor: VUKZ.border }}>
              <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>Checkpoints</span>
              {dir.checkpoints.map((cp, i) => (
                <div key={i} className="flex items-center gap-2 mt-1 text-[10px]" style={{ color: VUKZ.text }}>
                  <Calendar size={10} style={{ color: VUKZ.primary }} />
                  <span className="font-bold">{new Date(cp.date).toLocaleDateString('pt-BR')}</span>
                  <span>— {cp.test} ({cp.metric})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Tab: Tasks
// ═══════════════════════════════════════════════════════════════════════════════

const TasksTab = ({ tasks }) => {
  const grouped = useMemo(() => {
    const g = {};
    tasks.forEach((t) => {
      const key = t.owner_agent || 'other';
      if (!g[key]) g[key] = [];
      g[key].push(t);
    });
    return g;
  }, [tasks]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([agent, agentTasks]) => (
        <div key={agent} className="bg-white rounded-2xl border p-5" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
              <Users size={14} color="white" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{AGENT_LABELS[agent] || agent}</h3>
              <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                {agentTasks.filter((t) => t.status === 'completed').length}/{agentTasks.length} concluídas
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {agentTasks.map((task) => {
              const isCompleted = task.status === 'completed';
              return (
                <div
                  key={task.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${isCompleted ? 'opacity-60' : ''}`}
                  style={{ borderColor: VUKZ.border }}
                >
                  <div className="mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 size={16} style={{ color: VUKZ.success }} />
                    ) : (
                      <Clock size={16} style={{ color: VUKZ.warning }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold leading-snug" style={{ color: VUKZ.text }}>{task.title}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: VUKZ.textLight }}>{task.output_expected}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {task.category_id && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                          {task.category_id}
                        </span>
                      )}
                      {task.due_date && (
                        <span className="text-[9px] flex items-center gap-0.5" style={{ color: VUKZ.textLight }}>
                          <Calendar size={8} /> {new Date(task.due_date).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                      {task.alignment_score != null && (
                        <span className="text-[9px] font-bold" style={{ color: task.alignment_score >= 80 ? VUKZ.success : VUKZ.warning }}>
                          Score: {task.alignment_score}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Tab: Decisions
// ═══════════════════════════════════════════════════════════════════════════════

const DecisionsTab = ({ decisions }) => (
  <div className="space-y-3">
    {decisions.map((dec) => {
      const dStyle = DECISION_TYPE_COLORS[dec.decision_type] || DECISION_TYPE_COLORS.strategic;
      return (
        <div key={dec.id} className="bg-white rounded-2xl border p-5" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2 mb-2">
            <StatusBadge
              label={DECISION_TYPE_LABELS[dec.decision_type]}
              bg={dStyle.bg}
              text={dStyle.text}
            />
            {dec.category_id && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                {dec.category_id}
              </span>
            )}
            <span className="text-[9px] ml-auto" style={{ color: VUKZ.textLight }}>
              {dec.decided_at ? new Date(dec.decided_at).toLocaleDateString('pt-BR') : ''}
            </span>
          </div>
          <h4 className="text-sm font-extrabold mb-1" style={{ color: VUKZ.text }}>{dec.title}</h4>
          <p className="text-[11px] mb-2" style={{ color: VUKZ.textLight }}>{dec.description}</p>

          <div className="p-3 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
            <span className="text-[9px] font-bold" style={{ color: VUKZ.textLight }}>RACIONAL</span>
            <p className="text-[11px] leading-relaxed mt-0.5" style={{ color: VUKZ.text }}>{dec.rationale}</p>
          </div>

          <div className="flex items-center gap-3 mt-3 text-[10px]" style={{ color: VUKZ.textLight }}>
            <span>Responsável: <strong style={{ color: VUKZ.text }}>{dec.responsible}</strong></span>
            <span>·</span>
            <span>Decidido por: <strong style={{ color: VUKZ.text }}>{dec.decided_by}</strong></span>
          </div>
        </div>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Tab: Exceptions
// ═══════════════════════════════════════════════════════════════════════════════

const ExceptionsTab = ({ exceptions }) => (
  <div className="space-y-3">
    {exceptions.length === 0 ? (
      <div className="bg-white rounded-2xl border p-8 text-center" style={{ borderColor: VUKZ.border }}>
        <CheckCircle2 size={32} className="mx-auto mb-3" style={{ color: VUKZ.success }} />
        <h3 className="text-sm font-bold mb-1" style={{ color: VUKZ.text }}>Nenhuma exceção registrada</h3>
        <p className="text-[11px]" style={{ color: VUKZ.textLight }}>Todas as categorias seguem dentro das diretrizes</p>
      </div>
    ) : (
      exceptions.map((exc) => (
        <div key={exc.id} className="bg-white rounded-2xl border p-5" style={{ borderColor: exc.status === 'pending' ? '#f9a8d4' : VUKZ.border }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fce7f3', color: '#9d174d' }}>
                {exc.category_id}
              </span>
              <StatusBadge
                label={exc.status === 'pending' ? 'PENDENTE' : exc.status === 'approved' ? 'APROVADA' : 'NEGADA'}
                bg={exc.status === 'pending' ? '#fef3c7' : exc.status === 'approved' ? '#d1fae5' : '#fee2e2'}
                text={exc.status === 'pending' ? '#92400e' : exc.status === 'approved' ? '#065f46' : '#991b1b'}
              />
            </div>
            <span className="text-[9px]" style={{ color: VUKZ.textLight }}>
              {exc.requested_at ? new Date(exc.requested_at).toLocaleDateString('pt-BR') : ''}
            </span>
          </div>

          <div className="p-3 rounded-xl mb-3" style={{ backgroundColor: '#fdf2f8' }}>
            <span className="text-[9px] font-bold" style={{ color: '#9d174d' }}>REGRA VIOLADA</span>
            <p className="text-[11px] font-bold mt-0.5" style={{ color: VUKZ.text }}>{exc.rule_violated}</p>
          </div>

          <p className="text-[11px] mb-2" style={{ color: VUKZ.text }}>{exc.reason}</p>

          {exc.impact_expected && (
            <div className="p-3 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
              <span className="text-[9px] font-bold" style={{ color: VUKZ.textLight }}>IMPACTO ESPERADO</span>
              <p className="text-[11px] mt-0.5" style={{ color: VUKZ.text }}>{exc.impact_expected}</p>
            </div>
          )}

          <div className="flex items-center gap-3 mt-3 text-[10px]" style={{ color: VUKZ.textLight }}>
            <span>Solicitado por: <strong style={{ color: VUKZ.text }}>{exc.requested_by}</strong></span>
            {exc.decided_by && (
              <>
                <span>·</span>
                <span>Decidido por: <strong style={{ color: VUKZ.text }}>{exc.decided_by}</strong></span>
              </>
            )}
          </div>
        </div>
      ))
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

const GestorPlanPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [plan, setPlan] = useState(null);
  const [directives, setDirectives] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const dashboard = await getGestorDashboard();
        setPlan(dashboard);

        const planId = dashboard?.plan_id || 1;
        const [dirs, decs, tsks, excs] = await Promise.all([
          getPlanDirectives(planId),
          getPlanDecisions(planId),
          getPlanTasks(planId),
          getPlanExceptions(planId),
        ]);
        setDirectives(dirs);
        setDecisions(decs);
        setTasks(tsks);
        setExceptions(excs);
      } catch (err) {
        console.error('Error loading plan:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">

        {/* Back */}
        <Link to="/manager" className="inline-flex items-center gap-1 text-[11px] font-bold mb-4 hover:opacity-70 transition-opacity" style={{ color: VUKZ.textLight }}>
          <ArrowLeft size={14} /> Voltar ao Dashboard
        </Link>

        <PageHeader
          title="Plano Estratégico"
          subtitle={plan ? `${plan.plan_title} · v${plan.methodology_version}` : 'Carregando...'}
          badgeText="GESTOR"
          className="mb-6"
        />

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
            ))}
          </div>
        ) : plan ? (
          <>
            <TabBar active={activeTab} onChange={setActiveTab} />

            {activeTab === 'overview' && <OverviewTab plan={plan} />}
            {activeTab === 'directives' && <DirectivesTab directives={directives} />}
            {activeTab === 'tasks' && <TasksTab tasks={tasks} />}
            {activeTab === 'decisions' && <DecisionsTab decisions={decisions} />}
            {activeTab === 'exceptions' && <ExceptionsTab exceptions={exceptions} />}
          </>
        ) : (
          <div className="bg-white rounded-2xl border p-8 text-center" style={{ borderColor: VUKZ.border }}>
            <FileText size={32} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
            <h3 className="text-sm font-bold mb-1" style={{ color: VUKZ.text }}>Nenhum plano encontrado</h3>
            <p className="text-[11px]" style={{ color: VUKZ.textLight }}>Crie um plano estratégico para começar.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestorPlanPage;
